import React, { useEffect, useState } from 'react';
import {
  Alert,
  Grid,
  Paper,
  FormControl,
  TextField,
  InputLabel,
  Select,
  Typography,
  Button,
  MenuItem,
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { checkEmailValid } from 'utils/helper';
import {
  createMember,
  deleteMember,
  fetchMember,
  updateMember,
} from 'redux/actions';

const useStyles = makeStyles((theme) => ({
  root: {},
  paper: {
    paddingBottom: theme.spacing(3),
    borderRadius: '4px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f9fafc',
    padding: theme.spacing(1.5),
    color: '#A50028',
    borderBottom: 'solid 1px #eaeef1',
    borderRadius: '4px 4px 0 0',
    '&.detail p': {
      color: '#333',
      fontSize: '14px',
    },
  },
  expirationBadge: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1, 1.5),
    padding: theme.spacing(0.25, 0.5),
    width: 'fit-content',
    backgroundColor: '#f2f4f8',
    color: '#586065',
    fontSize: '0.6rem',
    fontWeight: 'bold',
    borderRadius: '4px',
    '& svg': {
      marginRight: theme.spacing(0.5),
      fontSize: '0.75rem',
    },
  },
  levelInfo: {
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    padding: theme.spacing(0.5, 1.5),
    '&.new': {
      marginTop: theme.spacing(2),
    },
  },
  levelBadge: {
    marginLeft: 'auto',
    padding: theme.spacing(0.25, 0.5),
    backgroundColor: '#337ab7',
    color: '#fff',
    fontSize: '0.75rem',
    borderRadius: '4px',
  },
  inputsPanel: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3, 1.5),
  },
  input: {
    margin: theme.spacing(1, 0),
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 1.5),
    marginTop: theme.spacing(3),
  },
  button: {
    backgroundColor: '#5b8d8b',
    color: '#fff',
    borderRadius: '4px',
    minWidth: '110px',
    '&:hover': {
      backgroundColor: '#1f3c3c',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    },
    '&.Mui-disabled': {
      color: '#fff',
      backgroundColor: '#5b8d8b',
      opacity: '0.6',
    },
    '&.delete': {
      marginLeft: theme.spacing(3),
      backgroundColor: '#A50028',
    },
  },
  level: {
    width: '100%',
  },
}));

const CreateNewUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const user = useSelector((state) => state?.user);
  const member = useSelector((state) => state?.member);
  const classes = useStyles();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [status, setStatus] = useState(0);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isNew, setIsNew] = useState(true);
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (params.id) {
      setIsNew(false);
      dispatch(fetchMember(params.id));
    }
  }, [params, dispatch]);

  useEffect(() => {
    if (member) {
      setName(member.full_name);
      setEmail(member.email);
    }
  }, [member]);

  const handleSubmit = () => {
    if (!checkEmailValid(email)) {
      return setIsValidEmail(false);
    }
    setIsValidEmail(true);

    if (member) {
      const payload = {
        user_id: member.user_id,
        full_name: name,
        email,
        level,
      };
      dispatch(updateMember(payload));
    } else {
      const payload = {
        full_name: name,
        email,
        level,
      };

      dispatch(createMember(payload));
    }
    navigate('/admin/users');
  };

  const handleDelete = () => {
    if (member) {
      dispatch(deleteMember(member.user_id));
      navigate('/admin/users');
    }
  };

  return (
    <Grid container className={classes.root} spacing={isNew ? 0 : 3}>
      <Grid item xs={6}>
        <Paper className={classes.paper}>
          <div className={classes.header}>
            <Typography>{isNew ? 'Create Account' : 'Jon Doe'}</Typography>
          </div>
          {!isNew && (
            <Typography className={classes.expirationBadge}>
              <CheckIcon />
              Non-Expiring
            </Typography>
          )}
          {user.level !== 4 && (
            <Typography
              className={isNew ? `${classes.levelInfo} new` : classes.levelInfo}
              component='div'
            >
              Level:
              <span className={classes.levelBadge}>0</span>&nbsp;- User
            </Typography>
          )}
          <div className={classes.inputsPanel}>
            {user.level === 4 && (
              <FormControl className={classes.input}>
                <InputLabel>Level</InputLabel>
                <Select
                  className={classes.level}
                  label='Level'
                  defaultValue={user.level}
                  onChange={(e) => setLevel(e.target.value)}
                >
                  <MenuItem value={1}>0: User(Use sets only)</MenuItem>
                  <MenuItem value={2}>
                    1: Co Instructor(Manage users only)
                  </MenuItem>
                  <MenuItem value={3}>
                    2: Developer(Manage content only)
                  </MenuItem>
                  <MenuItem value={4}>
                    3: Organization Admin(Manage users and content)
                  </MenuItem>
                </Select>
              </FormControl>
            )}
            {/* <FormControl className={classes.input}>
              <TextField
                required
                label='Organization'
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              />
            </FormControl> */}
            <FormControl className={classes.input}>
              <TextField
                required
                label='Full Name'
                value={name}
                onChange={(e) => setName(e.target.value)}
              ></TextField>
            </FormControl>
            <FormControl className={classes.input}>
              <TextField
                required
                error={!isValidEmail}
                label='Email'
                helperText={isValidEmail ? '' : 'Please enter correct email'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              ></TextField>
            </FormControl>
            {!isNew && (
              <FormControl className={classes.input}>
                <InputLabel id='status-label'>Status</InputLabel>
                <Select
                  labelId='status-label'
                  value={status}
                  label='Status'
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value={0}>Inactive</MenuItem>
                  <MenuItem value={1}>Active</MenuItem>
                </Select>
              </FormControl>
            )}
          </div>
          {isNew && (
            <Alert severity='info' icon={false}>
              The new user will be automatically emailed a{' '}
              <strong>temporary password</strong> to log in and get started
              using the system.
            </Alert>
          )}
          <div className={classes.actions}>
            <Button
              className={classes.button}
              onClick={handleSubmit}
              disabled={!name || !email}
            >
              Save Changes
            </Button>
            {!isNew && (
              <Button
                className={`${classes.button} delete`}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
          </div>
        </Paper>
      </Grid>
      {!isNew && (
        <Grid item xs={6}>
          <Paper className={classes.paper}>
            <div className={`${classes.header} detail`}>
              <Typography>
                <strong>Set Access / Status</strong>
                {` - (scroll for more)`}
              </Typography>
            </div>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default CreateNewUser;
